/**
 * useEmissionSubmit — F6 (Option B).
 *
 * Encapsulates the full Save-flow that previously lived inline as
 * `handleSubmit` in EmissionEntryForm.js (617 lines). Returns a single
 * `submit()` function the form can call from its Save button.
 *
 * Strict NO-LOGIC-CHANGE policy — the body was lifted byte-identically;
 * only the closure scope was reified into `ctx` and `setters` arguments.
 *
 * The hook orchestrates:
 *  - C7 multi-employee yearly + monthly create
 *  - Module dispatch via `categoryRegistry` (Scope 1/2 generic + per-category
 *    Scope 3 modules + biogenic + Stationary/Mobile/Fugitive)
 *  - `editingEmission` update path (PUT /emissions/{id})
 *  - Process Emissions through the configuration-driven module dispatch
 *  - Final fallback toast for unsupported categories
 *
 * Behaviour byte-identical: validation gate, toast messages, axios endpoints,
 * audit-history persistence, success/error semantics all preserved.
 */
import axios from 'axios';
import { toast } from 'sonner';

import { categoryRegistry } from '../../../../emissions';
import { MONTHS } from '../constants/emission-form-constants';
import { resolveDensityFieldState } from '../utils/unitHelpers';
import { isMonthlyEntryStarted } from '../utils/monthlyCompletion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createSubmissionBatchId = () => (
  globalThis.crypto?.randomUUID?.()
  || `emission-batch-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const getApiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || JSON.stringify(item)).join(', ');
  }
  return typeof detail === 'string' ? detail : fallback;
};

const formatSavedMonths = (monthKeys) => {
  const monthNames = monthKeys.map((monthKey) => (
    MONTHS.find((month) => month.key === monthKey)?.name || String(monthKey)
  ));
  if (monthNames.length < 2) return monthNames[0] || 'the selected month';
  if (monthNames.length === 2) return monthNames.join(' and ');
  return `${monthNames.slice(0, -1).join(', ')}, and ${monthNames[monthNames.length - 1]}`;
};

const applyOcrEmissionMetadata = (payload, ocrPrefillData) => {
  if (!ocrPrefillData) return payload;
  const canonicalPayload = { ...payload };
  ['naics_code', 'naics_label', 'naics_commodity', 'ef_database', 'accounting_rationale']
    .forEach((field) => delete canonicalPayload[field]);
  const invoiceNumber = String(ocrPrefillData.invoice_number || '').trim();
  return {
    ...canonicalPayload,
    source_of_information: invoiceNumber ? `Invoice No. ${invoiceNumber}` : canonicalPayload.source_of_information,
    record_source: 'OCR invoice upload',
  };
};

export function useEmissionSubmit(ctx) {
  const submit = async () => {
    const {
      facilityId, scope, category, categoryCode, fuelId,
      useCustomFuel, customFuelName, customEmissionFactor, customSource,
      recordSource,
      isSaving, scope3Method, spendCurrencyConversionMethod = 'standard', allocationMethod, scope3ActivityId, scope3ActivityType,
      scope3Subcategory, typeOfProduct, scope3CustomActivity, useCustomActivity, biogenicScopeSelection,
      employees, frequencyType, reportingYearType, reportingYear,
      monthlyData, yearlyData, processNames, responsiblePerson,
      responsiblePersonDesignation, responsiblePersonContact, notes, supplierName,
      supplierCode, employeeName, employeeId, assetName,
      fromLocation, toLocation, dynamicCategories, setIsSaving, isC7EmployeeCommuting,
      isProcessEmissions = false, requiresSubcategory, selectedFuel, filteredScope3Activities,
      dynamicInputFields, centralizedUnits, defaultUnit, canProceedToStep, getAuthHeader,
      onSuccess, getActualYearForMonth,
      buildDecisionInputs, editingEmission,
      decisionFieldValues,
      capabilities,
      calculateC7EmployeesForSave,
      isC6MultiTrip = false,
      c6Trips = { monthly: {}, yearly: [] },
      // Optional supplier context
      supplierContext = null,
      assignedReportingPeriod = null,
      // OCR context for finalize-import
      ocrPrefillData = null,
      // Evidence files are staged locally and only uploaded after record persistence.
      commitDraftEvidence = null,
    } = ctx;
    
    // Helper to finalize OCR import after successful emission save
    const finalizeOcrImport = async (emissionRecordIds) => {
      if (!ocrPrefillData?.line_item_id) return;
      
      try {
        console.log('[OCR Finalize] Calling finalize-import with:', {
          line_item_id: ocrPrefillData.line_item_id,
          emission_record_ids: emissionRecordIds
        });
        
        await axios.post(`${API}/ocr-invoice/finalize-import`, {
          line_item_id: ocrPrefillData.line_item_id,
          emission_record_ids: emissionRecordIds
        }, { headers: getAuthHeader() });
        
        console.log('[OCR Finalize] Successfully finalized OCR import');
      } catch (err) {
        console.error('[OCR Finalize] Failed to finalize import:', err);
        toast.error('GHG entry was saved, but the invoice could not yet be attached as evidence. The OCR row remains available for retry.');
      }
    };
    
    // All emission creation uses the canonical endpoint so the calculated
    // outputs, validation, audit history, and supplier safeguards share one
    // persistence path. Supplier submission and period-lock actions remain in
    // the supplier-assessment API.
    const apiBase = `${API}/emissions`;

    const rollbackSubmissionBatch = async (submissionBatchId) => {
      try {
        const response = await axios.post(`${API}/emissions/batch-rollback`, {
          submission_batch_id: submissionBatchId,
        }, { headers: getAuthHeader() });
        return { rolledBackCount: response.data?.rolled_back_count || 0 };
      } catch (error) {
        return { error: getApiErrorMessage(error, 'Unable to reverse the saved months') };
      }
    };

    // Link a calc-engine audit log entry to a newly created emission record.
    // Best-effort: failures are logged but never block the save flow.
    const linkAuditLog = async (auditLogId, emissionRecordId) => {
      if (!auditLogId || !emissionRecordId) return;
      try {
        await axios.post(`${API}/calc-engine/audit-log/link-emission`, {
          audit_log_id: auditLogId,
          emission_record_id: emissionRecordId,
        }, { headers: getAuthHeader() });
      } catch (err) {
        console.warn('[useEmissionSubmit] Failed to link audit log:', err);
      }
    };

    const commitSavedEvidence = async (savedRecords) => {
      if (!commitDraftEvidence || savedRecords.length === 0) return;
      try {
        await commitDraftEvidence(savedRecords);
      } catch (error) {
        console.error('[useEmissionSubmit] evidence commit failed:', error);
        toast.error(`The emission was saved, but ${error.message || 'some evidence could not be uploaded'}.`);
      }
    };

    // Prevent duplicate submissions
    if (isSaving) return;
    
    let submissionEmployees = employees;
    if (isC7EmployeeCommuting && employees.length > 0 && calculateC7EmployeesForSave) {
      setIsSaving(true);
      const calculation = await calculateC7EmployeesForSave();
      if (calculation?.error) {
        toast.error(`Nothing was saved. ${calculation.error}`);
        setIsSaving(false);
        return;
      }
      submissionEmployees = calculation?.employees || employees;
    }

    const c6TripRows = isC6MultiTrip
      ? (frequencyType === 'yearly'
        ? (c6Trips.yearly || []).map((data, index) => ({
          data,
          periodKey: 'yearly',
          tripNumber: index + 1,
          frequency: 'yearly',
        }))
        : Object.entries(c6Trips.monthly || {}).flatMap(([periodKey, trips]) => (
          (trips || []).map((data, index) => ({
            data,
            periodKey,
            tripNumber: index + 1,
            frequency: 'monthly',
          }))
        )))
      : [];
    const validation = canProceedToStep(4, {
      employees: submissionEmployees,
      multiTripRows: c6TripRows,
    }); // Final validation
    if (!validation.valid) {
      toast.error(validation.message);
      setIsSaving(false);
      return;
    }

    // Resolve Density from the actual quantity/reference unit pair at submit
    // time. This is method- and unit-driven, so monthly/yearly, standard/custom
    // fuels, and future categories share the same validation contract.
    const isProcessCategory = isProcessEmissions || categoryCode === 'process_emissions';
    if (dynamicInputFields.length > 0) {
      const rowsToValidate = isC6MultiTrip
        ? c6TripRows.map(({ periodKey, data }) => [periodKey, data])
        : frequencyType === 'yearly'
        ? [['yearly', yearlyData]]
        : Object.entries(monthlyData || {});
      for (const [periodKey, data] of rowsToValidate) {
        const calculationMethodology = decisionFieldValues?.calculation_methodology
          || data?.calculation_methodology
          || buildDecisionInputs?.(data)?.calculation_methodology;
        const densityState = resolveDensityFieldState({
          calculationMethodology,
          fields: dynamicInputFields,
          data,
          selectedFuel: useCustomFuel ? null : selectedFuel,
          centralizedUnits,
        });
        if (!densityState.visible || densityState.effectiveDensity) continue;
        {
          const monthName = periodKey === 'yearly'
            ? 'the annual entry'
            : (MONTHS.find((month) => month.key === periodKey)?.name || periodKey);
          toast.error(`Please enter Density (${densityState.densityUnit}) for ${monthName} because the quantity and factor units use different dimensions`);
          return;
        }
      }
    }

    setIsSaving(true); // Disable button immediately

    // Shared module-resolution helper (used by both monthly & yearly dispatch).
    // Returns the active category module (with `buildCreatePayload`) or null
    // if the scope/category combo is not yet wired through the registry.
    const resolveDispatchModule = () => {
      const cat = (category || '').toLowerCase();
      let mod = null;
      if (scope === 'scope3') {
        const codeMatch = cat.match(/^(c\d+)/);
        if (!codeMatch) return null;
        if (codeMatch[1] === 'c7') return null; // C7 has its own dedicated branch
        mod = categoryRegistry.get(codeMatch[1]);
      } else if (scope === 'scope1') {
        if (cat.includes('stationary')) mod = categoryRegistry.get('stationary_combustion');
        else if (cat.includes('mobile')) mod = categoryRegistry.get('mobile_combustion');
        else if (cat.includes('fugitive')) mod = categoryRegistry.get('fugitive_emissions');
        else mod = categoryRegistry.getGenericModule?.('scope1');
      } else if (scope === 'scope2') {
        mod = categoryRegistry.getGenericModule?.('scope2');
      } else if (scope === 'biogenic') {
        if (biogenicScopeSelection === 'scope3') {
          const codeMatch = cat.match(/^(c\d+)/);
          if (codeMatch && codeMatch[1] === 'c7') return null;
          mod = categoryRegistry.getGenericModule?.('scope3');
        } else if (biogenicScopeSelection === 'scope1') {
          mod = categoryRegistry.getGenericModule?.('scope1');
        }
      }
      return mod?.buildCreatePayload ? mod : null;
    };

    // Config-driven Scope 1/2 submissions use this same calculation contract
    // for a monthly row and the annual row. In particular, Custom Fuel EF
    // overrides and Process Emissions decision inputs must not diverge by
    // frequency before reaching the calculation engine.
    const calculateModuleRow = async ({ activeModule, data, baseCtx }) => {
      const {
        inputs,
        userOverrides,
        decisionInputs: extractedDecisionInputs = {},
        densityError,
        isCustomFuelReady,
        customFuelMissingFields = [],
      } = activeModule.extractInputsForCalcEngine(data, baseCtx);
      if (densityError) return { error: densityError };
      if (useCustomFuel && !isCustomFuelReady) {
        return { error: `Missing: ${customFuelMissingFields.join(', ') || 'Custom Fuel inputs'}` };
      }

      const { decisionInputs: moduleDecisionInputs, context, isScope3Like } = activeModule.buildDecisionContext(data, baseCtx);
      const decisionInputs = { ...moduleDecisionInputs, ...extractedDecisionInputs };
      const effectiveScopeForLookup = isScope3Like ? 'scope3' : scope;
      const categoryObj = dynamicCategories.find((item) => (
        item.scope_code === effectiveScopeForLookup
        && (item.code === categoryCode || (!categoryCode && item.name === category))
      ));

      if (isProcessCategory && !categoryObj?.id) {
        return { error: 'Process Emissions calculation configuration is unavailable' };
      }
      if (useCustomFuel && !categoryObj?.id) {
        return { error: 'Custom Fuel calculation category is unavailable' };
      }

      const result = {
        inputs,
        userOverrides,
        calculatedCO2: 0,
        calculatedCH4: 0,
        calculatedN2O: 0,
        calculatedCO2e: 0,
        resolvedFormulaId: null,
        formulaVersionId: null,
        decisionTreeVersionId: null,
        auditLogId: null,
      };
      if (!categoryObj?.id) return result;

      const effectiveOverrides = { ...userOverrides };
      if (useCustomFuel && inputs.ef_quantity) {
        effectiveOverrides.ef_quantity = inputs.ef_quantity;
        effectiveOverrides.emission_factor = inputs.ef_quantity;
      }

      try {
        const calcResp = await axios.post(`${API}/calc-engine/execute-by-category`, {
          category_id: categoryObj.id,
          decision_inputs: decisionInputs,
          inputs,
          context,
          user_overrides: effectiveOverrides,
          dry_run: false,
          ...(isScope3Like && scope3ActivityId && { scope3_ef_id: scope3ActivityId }),
        }, { headers: getAuthHeader() });
        if (!calcResp.data?.ok) return { error: 'calculation returned no result' };

        const calculated = calcResp.data;
        return {
          ...result,
          calculatedCO2: calculated.outputs?.co2?.value || calculated.co2_emissions || 0,
          calculatedCH4: calculated.outputs?.ch4?.value || calculated.ch4_emissions || 0,
          calculatedN2O: calculated.outputs?.n2o?.value || calculated.n2o_emissions || 0,
          calculatedCO2e: calculated.outputs?.co2e?.value || calculated.co2e_emissions || 0,
          resolvedFormulaId: calculated.resolved_formula?.id || calculated.formula_id || null,
          formulaVersionId: calculated.resolved_formula?.version_id || calculated.formula_version_id || null,
          decisionTreeVersionId: calculated.resolved_decision_tree?.version_id || null,
          auditLogId: calculated.audit_log_id || null,
        };
      } catch (error) {
        const detail = error.response?.data?.detail;
        return { error: typeof detail === 'string' ? detail : 'calculation failed' };
      }
    };

    try {
      const validProcesses = processNames.filter(p => p.name && p.name.trim() !== '');

      // C6 Business Travel: each trip uses the existing scoped calculation and
      // payload contract, then persists as its own record. A shared batch ID
      // preserves all-or-nothing behavior across every trip in this submission.
      if (isC6MultiTrip) {
        const c6Module = resolveDispatchModule();
        if (!c6Module) {
          toast.error('Business Travel is not available for direct submission. Please reload the page.');
          setIsSaving(false);
          return;
        }

        const startedTrips = c6TripRows.filter(({ data }) => (
          isMonthlyEntryStarted(data, dynamicInputFields)
        ));
        if (startedTrips.length === 0) {
          toast.error('Please enter data for at least one business travel trip');
          setIsSaving(false);
          return;
        }

        const submissionBatchId = createSubmissionBatchId();
        const preparedTrips = [];
        const errors = [];
        for (const tripRow of startedTrips) {
          const { data, periodKey, tripNumber, frequency } = tripRow;
          const reportingPeriod = frequency === 'yearly'
            ? (assignedReportingPeriod?.reporting_period || (reportingYearType === 'financial'
              ? `FY ${reportingYear}-${(parseInt(reportingYear) + 1).toString().slice(-2)}`
              : `CY${reportingYear}`))
            : `${getActualYearForMonth(periodKey)}-${periodKey}`;
          const baseCtx = {
            scope, category, categoryCode, capabilities, facilityId, fuelId, selectedFuel, useCustomFuel, customFuelName, customSource,
            recordSource, biogenicScopeSelection, scope3Method, spendCurrencyConversionMethod, allocationMethod, scope3ActivityId,
            scope3ActivityType, scope3Subcategory, typeOfProduct, scope3CustomActivity, useCustomActivity,
            supplierName, supplierCode, employeeName, employeeId, assetName,
            fromLocation: data.from_location || fromLocation,
            toLocation: data.to_location || toLocation,
            notes, responsiblePerson, responsiblePersonDesignation, responsiblePersonContact,
            validProcesses, dynamicInputFields, filteredScope3Activities, requiresSubcategory, centralizedUnits,
            defaultUnit, buildDecisionInputs, frequencyType: frequency,
            isOverrideCV: !!data.overrideCalorificValue,
            isOverrideDensity: !!data.overrideDensity,
            overrideEmissionFactorHeat: !!data.overrideEmissionFactorHeat,
            overrideJustification: data.calorificValueJustification || data.densityJustification || data.emissionFactorHeatJustification || '',
            calculatedCO2: 0, calculatedCH4: 0, calculatedN2O: 0, calculatedCO2e: 0,
            resolvedFormulaId: null, formulaVersionId: null, decisionTreeVersionId: null, reportingPeriod,
          };
          const calculation = await calculateModuleRow({ activeModule: c6Module, data, baseCtx });
          if (calculation.error) {
            const periodLabel = frequency === 'yearly'
              ? 'annual entry'
              : (MONTHS.find((month) => month.key === periodKey)?.name || periodKey);
            errors.push(`Trip ${tripNumber} in ${periodLabel}: ${calculation.error}`);
            continue;
          }
          preparedTrips.push({
            tripRow,
            calculation,
            payload: applyOcrEmissionMetadata({
              ...c6Module.buildCreatePayload(data, { ...baseCtx, ...calculation }),
              ...(frequency === 'yearly' && { frequency_type: 'yearly' }),
              submission_batch_id: submissionBatchId,
            }, ocrPrefillData),
          });
        }

        if (errors.length > 0) {
          toast.error(`Nothing was saved. ${errors.join(' • ')}. Fix the issue and try again.`, { duration: 10000 });
          setIsSaving(false);
          return;
        }

        const savedEmissionIds = [];
        const savedEvidenceRecords = [];
        let saveError = null;
        for (const { tripRow, calculation, payload } of preparedTrips) {
          try {
            const response = await axios.post(apiBase, payload, { headers: getAuthHeader() });
            if (response.data?.id) {
              savedEmissionIds.push(response.data.id);
              savedEvidenceRecords.push({
                id: response.data.id,
                evidences: tripRow.data?.evidences || [],
              });
              linkAuditLog(calculation.auditLogId, response.data.id);
            }
          } catch (error) {
            const periodLabel = tripRow.frequency === 'yearly'
              ? 'annual entry'
              : (MONTHS.find((month) => month.key === tripRow.periodKey)?.name || tripRow.periodKey);
            saveError = `Trip ${tripRow.tripNumber} in ${periodLabel}: ${getApiErrorMessage(error, 'Unable to save this record')}`;
            break;
          }
        }

        if (saveError) {
          const rollback = await rollbackSubmissionBatch(submissionBatchId);
          const rollbackMessage = rollback.error
            ? `Rollback issue: ${rollback.error}`
            : `${rollback.rolledBackCount} saved trip(s) were reverted.`;
          toast.error(`Nothing was saved. ${saveError}. ${rollbackMessage} Fix the issue and try again.`, { duration: 10000 });
          setIsSaving(false);
          return;
        }

        if (savedEmissionIds.length > 0) await finalizeOcrImport(savedEmissionIds);
        await commitSavedEvidence(savedEvidenceRecords);
        toast.success(`Created ${preparedTrips.length} business travel trip${preparedTrips.length === 1 ? '' : 's'}`);
        onSuccess?.();
        setIsSaving(false);
        return;
      }
      
      // ===========================================
      // C7 EMPLOYEE COMMUTING HANDLING (Phase F: module dispatch)
      // ===========================================
      // Multi-employee yearly + monthly CREATE flow.
      // Logic lives in /modules/emissions/categories/C7EmployeeCommuting/create.js
      // Dedicated endpoints: /api/emissions/c7/yearly and /api/emissions/c7/month
      if (isC7EmployeeCommuting && employees.length > 0) {
        const c7Module = categoryRegistry.get('c7');
        if (!c7Module?.buildCreatePayload) {
          toast.error('C7 module not registered. Please reload the page.');
          setIsSaving(false);
          return;
        }

        const c7Ctx = {
          employees: submissionEmployees,
          frequencyType,
          facilityId,
          reportingYearType,
          reportingYear,
          scope3Method,
          scope3ActivityId,
          scope3ActivityType,
          scope3CustomActivity,
          useCustomActivity,
          filteredScope3Activities,
          notes,
          recordSource,
          supplierName,
          supplierCode,
          responsiblePerson,
          responsiblePersonDesignation,
          responsiblePersonContact,
          processNames,
          validProcesses,
          getActualYearForMonth,
        };

        // 1. Module-owned validation (employee names + per-mode data presence + calc check)
        const c7Validation = c7Module.validateCreateSubmission(c7Ctx);
        if (!c7Validation.valid) {
          toast.error(c7Validation.errorMessage);
          setIsSaving(false);
          return;
        }

        // 2. Module-owned payload construction (yearly: single payload, monthly: list of payloads)
        const c7Built = c7Module.buildCreatePayload(null, c7Ctx);

        // 3. POST + UI semantics (kept here — orchestration responsibility of the page/form)
        if (c7Built.mode === 'yearly') {
          try {
            const response = await axios.post(`${API}${c7Built.endpoint}`, applyOcrEmissionMetadata(c7Built.payload, ocrPrefillData), {
              headers: getAuthHeader(),
            });
            if (response.data?.id) await finalizeOcrImport([response.data.id]);
            if (response.data?.id) {
              await commitSavedEvidence([{
                id: response.data.id,
                drafts: submissionEmployees.flatMap((employee) => (
                  (employee.yearly_data?.evidences || []).map((evidence) => ({
                    id: response.data.id,
                    evidence,
                    employeeId: employee.id,
                    periodKey: 'yearly',
                  }))
                )),
              }]);
            }
            toast.success(`Created yearly C7 Employee Commuting record for ${c7Built.reportingPeriod}`);
            onSuccess?.();
          } catch (error) {
            console.error('Error saving yearly C7 emission:', error);
            const detail = error.response?.data?.detail;
            const errorMsg = Array.isArray(detail)
              ? detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ')
              : (typeof detail === 'string' ? detail : 'Failed to save yearly C7 emission');
            toast.error(errorMsg);
          } finally {
            setIsSaving(false);
          }
          return;
        }

        // monthly: post each month-payload sequentially
        if (!c7Built.payloads || c7Built.payloads.length === 0) {
          toast.error('No valid monthly data to save');
          setIsSaving(false);
          return;
        }

        let totalCo2e = 0;
        const savedEmissionIds = [];
        const savedEvidenceRecords = [];
        const submissionBatchId = createSubmissionBatchId();
        let saveError = null;
        for (const { monthKey, monthCo2e, payload } of c7Built.payloads) {
          totalCo2e += monthCo2e;
          try {
            const response = await axios.post(`${API}${c7Built.endpoint}`, applyOcrEmissionMetadata({
              ...payload,
              submission_batch_id: submissionBatchId,
            }, ocrPrefillData), {
              headers: getAuthHeader(),
            });
            if (response.data?.id) {
              savedEmissionIds.push(response.data.id);
              savedEvidenceRecords.push({
                id: response.data.id,
                drafts: submissionEmployees.flatMap((employee) => (
                  (employee.monthly_data?.[monthKey]?.evidences || []).map((evidence) => ({
                    id: response.data.id,
                    evidence,
                    employeeId: employee.id,
                    periodKey: monthKey,
                  }))
                )),
              });
            }
          } catch (err) {
            console.error(`[C7] Failed to save ${monthKey}:`, err);
            saveError = `${monthKey}: ${getApiErrorMessage(err, 'Unable to save this month')}`;
            break;
          }
        }

        if (saveError) {
          const rollback = await rollbackSubmissionBatch(submissionBatchId);
          const rollbackMessage = rollback.error
            ? `Rollback issue: ${rollback.error}`
            : `${rollback.rolledBackCount} saved month(s) were reverted.`;
          toast.error(`Nothing was saved. ${saveError}. ${rollbackMessage} Fix the issue and try again.`, { duration: 10000 });
          setIsSaving(false);
          return;
        }

        if (savedEmissionIds.length > 0) await finalizeOcrImport(savedEmissionIds);
        await commitSavedEvidence(savedEvidenceRecords);

        toast.success(`Saved ${formatSavedMonths(c7Built.payloads.map(({ monthKey }) => monthKey))} for ${submissionEmployees.length} employee(s) (${totalCo2e.toFixed(4)} tCO₂e total)`);
        if (typeof onSuccess === 'function') onSuccess();

        setIsSaving(false);
        return;
      }
      
      // ===========================================
      // YEARLY FREQUENCY HANDLING (New)
      // ===========================================
      if (frequencyType === 'yearly') {
        // Build reporting period string for yearly
        const yearlyReportingPeriod = assignedReportingPeriod?.reporting_period || (reportingYearType === 'financial' 
          ? `FY ${reportingYear}-${(parseInt(reportingYear) + 1).toString().slice(-2)}`
          : `CY${reportingYear}`);
        
        // Validate yearly data has at least one value
        let hasYearlyData = false;
        if (dynamicInputFields.length > 0) {
          const requiredFields = dynamicInputFields.filter(f => !f.isOverride && !f.presentationOnly);
          hasYearlyData = requiredFields.some(f => {
            const value = yearlyData[f.variable] || yearlyData[f.fieldKey];
            return value && parseFloat(value) > 0;
          });
        } else {
          hasYearlyData = yearlyData.quantity && parseFloat(yearlyData.quantity) > 0;
        }
        
        if (!hasYearlyData) {
          toast.error('Please enter annual data');
          setIsSaving(false);
          return;
        }
        
        try {
          if (dynamicInputFields.length > 0) {
            // ============================================================
            // YEARLY DISPATCH (post-Phase F: module-driven, single record)
            // ============================================================
            // Mirrors the monthly dispatch but runs ONCE with `yearlyData`
            // as the row and `yearlyReportingPeriod` as the reporting period.
            // Module resolution follows the same scope/category/biogenic
            // logic; payload shape matches modular monthly + adds
            // `frequency_type: 'yearly'` for backend differentiation.
            const yearlyMod = resolveDispatchModule();

            if (!yearlyMod) {
              console.error('[EmissionEntryForm] No module dispatched for yearly', { scope, category, biogenicScopeSelection });
              toast.error('This category is not yet supported for yearly submission. Please reload the page or contact support.');
              setIsSaving(false);
              return;
            }

            // Module-owned validation (same context shape as monthly dispatch)
            const yModValidation = yearlyMod.validateCreateSubmission({
              formData: { asset_name: assetName },
              processNames,
              assetName,
              fuelId,
              useCustomFuel,
              customFuelName,
              isOverrideCV: false,
              isOverrideDensity: false,
              overrideEmissionFactorHeat: false,
              overrideJustification: '',
              scope,
              category,
              capabilities,
              buildDecisionInputs,
            });
            if (!yModValidation.valid) {
              toast.error(yModValidation.errorMessage);
              setIsSaving(false);
              return;
            }

            const yBaseCtx = {
              scope, category, categoryCode, capabilities, facilityId, fuelId, selectedFuel, useCustomFuel, customFuelName, customSource,
              recordSource,
              biogenicScopeSelection,
              scope3Method, spendCurrencyConversionMethod, allocationMethod, scope3ActivityId, scope3ActivityType, scope3Subcategory,
              typeOfProduct,
              scope3CustomActivity, useCustomActivity,
              supplierName, supplierCode, employeeName, employeeId,
              assetName, fromLocation, toLocation,
              notes, responsiblePerson, responsiblePersonDesignation, responsiblePersonContact,
              validProcesses,
              dynamicInputFields,
              filteredScope3Activities, requiresSubcategory, centralizedUnits,
              defaultUnit,
              buildDecisionInputs,
              frequencyType,
              // Per-row override flags read from yearlyData (yearly has a single row).
              isOverrideCV: !!yearlyData.overrideCalorificValue,
              isOverrideDensity: !!yearlyData.overrideDensity,
              overrideEmissionFactorHeat: !!yearlyData.overrideEmissionFactorHeat,
              overrideJustification: yearlyData.calorificValueJustification || yearlyData.densityJustification || yearlyData.emissionFactorHeatJustification || '',
              calculatedCO2: 0, calculatedCH4: 0, calculatedN2O: 0, calculatedCO2e: 0,
              resolvedFormulaId: null,
              formulaVersionId: null,
              decisionTreeVersionId: null,
              reportingPeriod: yearlyReportingPeriod,
            };

            const yearlyCalculation = await calculateModuleRow({
              activeModule: yearlyMod,
              data: yearlyData,
              baseCtx: yBaseCtx,
            });
            if (yearlyCalculation.error) {
              toast.error(yearlyCalculation.error);
              setIsSaving(false);
              return;
            }

            const yPayload = applyOcrEmissionMetadata({
              ...yearlyMod.buildCreatePayload(yearlyData, {
                ...yBaseCtx,
                ...yearlyCalculation,
              }),
              // Yearly-only marker (legacy parity)
              frequency_type: 'yearly',
          }, ocrPrefillData);

            const yResp = await axios.post(apiBase, yPayload, { headers: getAuthHeader() });
            if (yResp.data?.id) {
              linkAuditLog(yearlyCalculation.auditLogId, yResp.data.id);
              await finalizeOcrImport([yResp.data.id]);
              await commitSavedEvidence([{
                id: yResp.data.id,
                evidences: yearlyData.evidences || [],
              }]);
            }
            toast.success(`Created yearly emission record for ${yearlyReportingPeriod}`);
            onSuccess?.();
          }
        } catch (error) {
          console.error('Error saving yearly emission:', error);
          const detail = error.response?.data?.detail;
          const errorMsg = Array.isArray(detail) 
            ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
            : (typeof detail === 'string' ? detail : 'Failed to save yearly emission');
          toast.error(errorMsg);
        } finally {
          setIsSaving(false);
        }
        return;
      }
      
      // ===========================================
      // MONTHLY FREQUENCY HANDLING (Existing)
      // ===========================================
      // Filter months with configuration-driven dynamic field data.
      let monthsWithData;
      if (dynamicInputFields.length > 0) {
        // For dynamic form config, check if any required field (non-override) has value
        const requiredFields = dynamicInputFields.filter(f => !f.isOverride && !f.presentationOnly);
        monthsWithData = Object.entries(monthlyData).filter(([_, data]) => {
          return requiredFields.some(field => {
            const value = data?.[field.variable] || data?.[field.fieldKey];
            return value && parseFloat(value) > 0;
          });
        });
      } else {
        // No dynamic fields - should not happen if form is loaded correctly
        monthsWithData = [];
      }

      if (monthsWithData.length === 0) {
        toast.error('Please enter data for at least one month');
        setIsSaving(false);
        return;
      }

      // ===========================================
      // CREATE MIGRATION PHASES C/D/E/F — Module dispatch
      // ===========================================
      // Routes through module helpers when:
      //   - frequencyType === 'monthly'
      //   - scope is scope1/scope2/scope3 OR biogenic (Phase F)
      //   - active module exposes buildCreatePayload
      //   - category is NOT C7 (multi-employee — has its own dedicated branch)
      const dispatchActiveModule = frequencyType === 'monthly' ? resolveDispatchModule() : null;

      if (dispatchActiveModule) {
        // 1. Module-owned validation
        // Note: per-month override flags (CV/density/EFH) live in monthlyData[m]
        // and are validated inside the per-month loop below. Pass false at
        // submission gate; per-row gates re-check via data.* in the loop.
        const modValidation = dispatchActiveModule.validateCreateSubmission({
          formData: { asset_name: assetName },
          processNames,
          assetName,
          fuelId,
          useCustomFuel,
          customFuelName,
          isOverrideCV: false,
          isOverrideDensity: false,
          overrideEmissionFactorHeat: false,
          overrideJustification: '',
          scope,
          category,
          capabilities,
          buildDecisionInputs,
        });
        if (!modValidation.valid) {
          toast.error(modValidation.errorMessage);
          setIsSaving(false);
          return;
        }

        const errors = [];
        const preparedRows = [];
        const submissionBatchId = createSubmissionBatchId();
        for (const [monthKey, data] of monthsWithData) {
          const actualYear = getActualYearForMonth(monthKey);
          const reportingPeriod = `${actualYear}-${monthKey}`;

          const baseCtx = {
            scope, category, categoryCode, capabilities, facilityId, fuelId, selectedFuel, useCustomFuel, customFuelName, customSource,
            recordSource,
            biogenicScopeSelection,
            scope3Method, spendCurrencyConversionMethod, allocationMethod, scope3ActivityId, scope3ActivityType, scope3Subcategory,
            typeOfProduct,
            scope3CustomActivity, useCustomActivity,
            supplierName, supplierCode, employeeName, employeeId,
            assetName, fromLocation, toLocation,
            notes, responsiblePerson, responsiblePersonDesignation, responsiblePersonContact,
            validProcesses,
            dynamicInputFields,
            filteredScope3Activities, requiresSubcategory, centralizedUnits,
            defaultUnit,
            buildDecisionInputs,
            frequencyType,
            // Per-month CV/density override flags read from `data` (the row).
            // Pass row-level flags so Scope1Create payload sets override_justification correctly.
            isOverrideCV: !!data.overrideCalorificValue,
            isOverrideDensity: !!data.overrideDensity,
            overrideEmissionFactorHeat: !!data.overrideEmissionFactorHeat,
            overrideJustification: data.calorificValueJustification || data.densityJustification || data.emissionFactorHeatJustification || '',
            calculatedCO2: 0, calculatedCH4: 0, calculatedN2O: 0, calculatedCO2e: 0,
            resolvedFormulaId: null,
            formulaVersionId: null,
            decisionTreeVersionId: null,
            reportingPeriod,
          };

          const rowCalculation = await calculateModuleRow({
            activeModule: dispatchActiveModule,
            data,
            baseCtx,
          });
          if (rowCalculation.error) {
            errors.push(`${MONTHS.find(m => m.key === monthKey)?.name}: ${rowCalculation.error}`);
            continue;
          }

          preparedRows.push({
            monthKey,
            data,
            calculation: rowCalculation,
            payload: applyOcrEmissionMetadata({
              ...dispatchActiveModule.buildCreatePayload(data, {
                ...baseCtx,
                ...rowCalculation,
              }),
              submission_batch_id: submissionBatchId,
            }, ocrPrefillData),
          });
        }

        if (errors.length > 0) {
          toast.error(`Nothing was saved. ${errors.join(' • ')}. Fix the issue and try again.`, { duration: 10000 });
          setIsSaving(false);
          return;
        }

        const savedEmissionIds = [];
        const savedEvidenceRecords = [];
        let saveError = null;
        for (const { monthKey, data, calculation, payload } of preparedRows) {
          try {
            const response = await axios.post(apiBase, payload, { headers: getAuthHeader() });
            if (response.data?.id) {
              savedEmissionIds.push(response.data.id);
              savedEvidenceRecords.push({
                id: response.data.id,
                evidences: data.evidences || [],
              });
              linkAuditLog(calculation.auditLogId, response.data.id);
            }
          } catch (err) {
            const monthName = MONTHS.find(m => m.key === monthKey)?.name || monthKey;
            saveError = `${monthName}: ${getApiErrorMessage(err, 'Unable to save this record')}`;
            break;
          }
        }

        if (saveError) {
          const rollback = await rollbackSubmissionBatch(submissionBatchId);
          const rollbackMessage = rollback.error
            ? `Rollback issue: ${rollback.error}`
            : `${rollback.rolledBackCount} saved month(s) were reverted.`;
          toast.error(`Nothing was saved. ${saveError}. ${rollbackMessage} Fix the issue and try again.`, { duration: 10000 });
          setIsSaving(false);
          return;
        }

        if (savedEmissionIds.length > 0 && ocrPrefillData?.line_item_id) {
          await finalizeOcrImport(savedEmissionIds);
        }
        await commitSavedEvidence(savedEvidenceRecords);
        toast.success(`Created emissions for ${formatSavedMonths(preparedRows.map(({ monthKey }) => monthKey))}`);
        onSuccess?.();
        setIsSaving(false);
        return;
      }

      // ===========================================
      // DEFENSIVE FALLBACK (post-Phase F)
      // ===========================================
      // The dispatch block above covers every reachable monthly path:
      //   - Scope 1 (Stationary/Mobile/Fugitive + Generic), Scope 2 (Generic),
      //     Scope 3 flat (C1–C6, C8–C15), biogenic+scope1, biogenic+scope3.
      //   - C7 multi-employee returns early in its own dedicated branch above.
      // If we reach here, no module matched — surface a clear error so the
      // bug is observable instead of silently producing no record.
      console.error('[EmissionEntryForm] No module dispatched for', { scope, category, frequencyType, biogenicScopeSelection });
      toast.error('This category is not yet supported for direct submission. Please reload the page or contact support.');
    } catch (error) {
      // Temporary diagnostic: surface the underlying exception in DevTools so
      // we can identify the precise root cause instead of a generic toast.
      // Remove once the failing path is identified and patched.
      console.error('[useEmissionSubmit] save failed:', error, {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        stack: error?.stack,
      });
      toast.error('Failed to save emissions. Please try again.');
    } finally {
      setIsSaving(false); // Re-enable button after completion
    }
  };

  return { submit };
}

export default useEmissionSubmit;
